import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth, useAuthActions } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { MetadataList } from '../../shared/detail/MetadataList';
import { PageHeader } from '../../shared/layout/PageHeader';
import { ActiveSessionsPanel } from './ActiveSessionsPanel';
import { AuthenticationUnavailable } from './AuthenticationUnavailable';

export function ProfilePage({ dataSource }: { readonly dataSource: DataSource }) {
  const auth = useAuth();
  const { t } = useTranslation();

  if (auth.status !== 'authenticated') return null;
  const principal = auth.principal;

  return (
    <Stack spacing={3}>
      <PageHeader subtitle={t('scaffold.scopes.authProfile')} title={t('profile.title')} />
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Stack spacing={2}>
          <MetadataList
            items={[
              { label: t('profile.fields.name'), value: principal.actor.displayName },
              { label: t('profile.fields.username'), value: principal.username },
              {
                label: t('profile.fields.email'),
                value: principal.email ?? t('common.notAvailable'),
              },
              { label: t('profile.fields.role'), value: principal.role },
              {
                label: t('profile.fields.authMode'),
                value:
                  dataSource === 'mock'
                    ? t('auth.mockMode')
                    : t('profile.fields.identityProviderMode'),
              },
              {
                label: t('profile.fields.partnerId'),
                value: principal.scope.partnerId ?? t('common.notAvailable'),
              },
              {
                label: t('profile.fields.agentId'),
                value: principal.scope.agentId ?? t('common.notAvailable'),
              },
              {
                label: t('profile.fields.permissionProfileId'),
                value: principal.scope.permissionProfileId ?? t('common.notAvailable'),
              },
            ]}
          />
          <div>
            <Typography color="text.secondary" component="h2" variant="caption">
              {t('profile.fields.permissions')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
              {principal.permissions.map((permission) => (
                <Chip key={permission} label={permission} size="small" />
              ))}
            </Stack>
          </div>
        </Stack>
      </Paper>

      {dataSource === 'mock' ? (
        <ActiveSessionsPanel />
      ) : dataSource === 'real' ? (
        <AuthenticationUnavailable
          items={[
            t('profile.identityProvider.password'),
            t('profile.identityProvider.mfa'),
            t('profile.identityProvider.login'),
            t('profile.identityProvider.refresh'),
            t('profile.identityProvider.sessions'),
          ]}
        />
      ) : (
        <Alert severity="warning">{t('capability.backendUnavailable')}</Alert>
      )}

      <SignOutSection dataSource={dataSource} />
    </Stack>
  );
}

function SignOutSection({ dataSource }: { readonly dataSource: DataSource }) {
  const { signOut } = useAuthActions();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const handleSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Stack spacing={1.5}>
        <Typography component="h2" variant="subtitle1">
          {t('profile.signOut.title')}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {dataSource === 'mock'
            ? t('profile.signOut.mockDescription')
            : t('profile.signOut.description')}
        </Typography>
        <Button
          loading={busy}
          onClick={() => void handleSignOut()}
          sx={{ alignSelf: 'flex-start' }}
          variant="outlined"
        >
          {dataSource === 'mock' ? t('profile.signOut.mockAction') : t('profile.signOut.action')}
        </Button>
      </Stack>
    </Paper>
  );
}
